import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Zap } from "lucide-react";

export default function TwoArms() {
  return (
    <section id="two-arms" className="section border-b border-[var(--owrs-border)]">
      <div className="container-owrs">
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="overline mb-4">Two Specialisms · One Workshop</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05]">
            The same precision, <br />
            <span className="text-[var(--owrs-text-muted)]">applied to two worlds.</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[var(--owrs-text-muted)] leading-relaxed">
            Our foundations are in racing — and that's where the attention to detail and precision
            at the core of everything we do was forged. Today, those same standards are applied
            equally across the board, from the Porsches and Land Rovers we've always cared for,
            to the Teslas, Audis and BMWs of the next generation.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:gap-6">
          <ArmCard
            to="/performance"
            icon={<Award className="h-5 w-5" />}
            tag="Established expertise"
            title="Performance & Prestige"
            blurb="Porsche, Land Rover and performance car specialists. Road car servicing with the meticulous care we built our reputation on — through to complete bespoke race builds."
            cta="See Performance Services"
          />
          <ArmCard
            to="/ev"
            icon={<Zap className="h-5 w-5" />}
            tag="The new era"
            title="EV & Hybrid"
            blurb="A dedicated EV and hybrid arm — IMI-trained in HV safety and repair procedures. Tesla, Porsche Taycan, Audi e-tron, BMW i3 and beyond, all looked after in-house."
            cta="See EV & Hybrid Services"
            accent
          />
        </div>
      </div>
    </section>
  );
}

function ArmCard({ to, icon, tag, title, blurb, cta, accent }) {
  return (
    <Link
      to={to}
      className={`group relative overflow-hidden text-left p-5 sm:p-8 lg:p-12 border transition-all duration-500 ${
        accent
          ? "border-[var(--owrs-accent)]/50 bg-[var(--owrs-surface)] hover:border-[var(--owrs-accent)]"
          : "border-[var(--owrs-border-strong)] bg-[var(--owrs-surface)] hover:border-[var(--owrs-text-muted)]"
      }`}
    >
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div
          className={`h-10 w-10 sm:h-12 sm:w-12 inline-flex items-center justify-center border ${
            accent
              ? "bg-[var(--owrs-accent)] text-[var(--owrs-accent-ink)] border-[var(--owrs-accent)]"
              : "border-[var(--owrs-border-strong)] text-[var(--owrs-text)]"
          }`}
        >
          {icon}
        </div>
        <span className="overline hidden sm:inline">{tag}</span>
      </div>
      <h3 className="font-display text-xl sm:text-3xl lg:text-4xl font-medium tracking-tight mb-3 sm:mb-4">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-[var(--owrs-text-muted)] leading-relaxed mb-6 sm:mb-8 line-clamp-4 sm:line-clamp-none">
        {blurb}
      </p>
      <div className="inline-flex items-center gap-2 text-xs sm:text-sm text-[var(--owrs-accent)] group-hover:gap-3 transition-all">
        <span className="hidden sm:inline">{cta}</span>
        <span className="sm:hidden">Learn more</span>
        <ArrowRight className="h-4 w-4" />
      </div>
    </Link>
  );
}
